import React, { useEffect, useRef, useState } from "react";
import { mediaAdmin } from "@/lib/adminApi";
import { asset } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Upload, Film } from "lucide-react";

export default function MediaManager() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState({ title: "", category: "General", tags: "", orientation: "portrait" });
  const [video, setVideo] = useState({ title: "", url: "", category: "General", tags: "" });
  const fileRef = useRef(null);

  const load = () => mediaAdmin.list().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const doUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose an image first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
      await mediaAdmin.upload(fd);
      toast.success("Image uploaded");
      fileRef.current.value = "";
      setMeta({ title: "", category: "General", tags: "", orientation: "portrait" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addVideo = async (e) => {
    e.preventDefault();
    if (!video.url) { toast.error("Paste a video URL"); return; }
    await mediaAdmin.addVideo(video);
    toast.success("Video added");
    setVideo({ title: "", url: "", category: "General", tags: "" });
    load();
  };

  const remove = async (id) => {
    await mediaAdmin.remove(id);
    toast.success("Removed");
    load();
  };

  const input = "w-full border border-aria-travertine bg-aria-ivory px-3 py-2 text-sm text-aria-ink focus:border-aria-brass focus:outline-none";

  return (
    <div data-testid="admin-media">
      <div className="eyebrow mb-3">Library</div>
      <h1 className="font-display text-5xl text-aria-ink">Media</h1>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <form onSubmit={doUpload} className="border border-aria-travertine bg-aria-ivory p-6" data-testid="media-upload-form">
          <div className="mb-4 flex items-center gap-2 font-display text-2xl text-aria-ink"><Upload className="h-5 w-5" /> Upload image</div>
          <input ref={fileRef} type="file" accept="image/*" data-testid="media-file" className="mb-4 text-sm" />
          <div className="grid grid-cols-2 gap-3">
            <input className={input} placeholder="Title" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} data-testid="media-title" />
            <input className={input} placeholder="Category" value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })} />
            <input className={input} placeholder="Tags (comma separated)" value={meta.tags} onChange={(e) => setMeta({ ...meta, tags: e.target.value })} />
            <select className={input} value={meta.orientation} onChange={(e) => setMeta({ ...meta, orientation: e.target.value })}>
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
              <option value="square">Square</option>
            </select>
          </div>
          <button disabled={uploading} data-testid="media-upload-submit" className="mt-5 bg-aria-ink px-6 py-3 text-[0.68rem] uppercase tracking-[0.2em] text-aria-ivory disabled:opacity-50">
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </form>

        <form onSubmit={addVideo} className="border border-aria-travertine bg-aria-ivory p-6">
          <div className="mb-4 flex items-center gap-2 font-display text-2xl text-aria-ink"><Film className="h-5 w-5" /> Add video (link)</div>
          <div className="grid grid-cols-1 gap-3">
            <input className={input} placeholder="Video URL (Vimeo / YouTube / MP4)" value={video.url} onChange={(e) => setVideo({ ...video, url: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <input className={input} placeholder="Title" value={video.title} onChange={(e) => setVideo({ ...video, title: e.target.value })} />
              <input className={input} placeholder="Category" value={video.category} onChange={(e) => setVideo({ ...video, category: e.target.value })} />
            </div>
          </div>
          <button className="mt-5 bg-aria-ink px-6 py-3 text-[0.68rem] uppercase tracking-[0.2em] text-aria-ivory">Add video</button>
        </form>
      </div>

      <div className="mt-10 columns-2 gap-4 md:columns-4 [column-fill:_balance]">
        {items.map((m) => (
          <div key={m.id} className="group relative mb-4 break-inside-avoid overflow-hidden border border-aria-travertine bg-aria-ivory">
            {m.kind === "video" ? (
              <div className="flex aspect-video items-center justify-center bg-aria-ink text-aria-ivory/70"><Film className="h-8 w-8" /></div>
            ) : (
              <img src={asset(m.url)} alt={m.alt} loading="lazy" className="w-full object-cover" />
            )}
            <div className="flex items-center justify-between px-3 py-2">
              <span className="truncate text-[0.6rem] uppercase tracking-[0.14em] text-aria-muted">{m.category}</span>
              <button onClick={() => remove(m.id)} data-testid={`media-delete-${m.id}`} className="text-aria-muted hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
